import { put, call } from "@redux-saga/core/effects";
import moment from "moment";
import { toast } from "react-toastify";
import i18n from "../../../i18n/i18n";
import { getAPIConfig } from "../../../util/api-config";
import store from "../../configureStore";
import {
  setCardWithHistory,
  setExtendedCard,
  setExtendedCardForPerson,
  setRegisterCardForm,
  setExtendedCards,
  GET_CARD_WITH_HISTORY_FOR_PERSON,
  setHasHiddenTransactions,
  GET_EXTENDED_CARD,
} from "../../reducers/cardReducer";
import confirmSaga from "./modalSagas";
import { handleRegisterOptIn, handleRegisterOptOut } from "./personSagas";
import { getSuccessToast, handleError } from "../../../util/errorHandler";
import { isPNRandElder } from "../../../util/identity-validators";
import { addInfoMessage } from "../../reducers/messageReducer";
import {
  CardApi,
  CardWithHistoryDTO,
  ExtendedCardDTO,
} from "../../../generated/api";
import { filterTransactions } from "../../../util/filter";
import { setLoading } from "../../reducers/loadingReducer";

function setUpClient() {
  return new CardApi(getAPIConfig(store.getState().settings.internalSettings));
}

export function* handleRegisterCardModal(action: any): any {
  try {
    yield put(setRegisterCardForm(action.registerCardForm));
    yield call(confirmSaga, "registerCard");
  } catch (error) {
   yield handleError(error);
  }
}

function* getCardsAfterAction(action: any) {
  var params = {
    carePrincipalId: action.params.carePrincipalId,
    personId: null,
    cardId: null,
  };
  if (!action.params.value) {
    params = action.searchForm;
    yield put({ type: GET_CARD_WITH_HISTORY_FOR_PERSON, params });
  } else if (action.params.value === "personId") {
    params.personId = action.params.searchValue;
    yield put({ type: "GET_ALL_CARDS_FOR_PERSON", params });
  } else {
    params.cardId = action.params.searchValue;
    yield put({ type: "GET_ALL_CARDS_FOR_ID", params });
  }
}

function* filterAllTransactions(extendedCard: ExtendedCardDTO) {
  if (!store.getState().person.cardConsent.readConsent || store.getState().person.cardConsent.readConsentOptOut) {
    const transactionCount =
      (extendedCard.transactions ? extendedCard?.transactions?.length : 0) +
      (extendedCard?.expiredTransactions
        ? extendedCard?.expiredTransactions?.length
        : 0);
    extendedCard.transactions = filterTransactions(
      extendedCard.transactions,
      store.getState().session.currentUser.healthCareProviderHsaId
    );
    extendedCard.expiredTransactions = filterTransactions(
      extendedCard.expiredTransactions,
      store.getState().session.currentUser.healthCareProviderHsaId
    );
    const newTransactionCount =
      (extendedCard?.transactions ? extendedCard?.transactions?.length : 0) +
      (extendedCard?.expiredTransactions
        ? extendedCard?.expiredTransactions?.length
        : 0);
    yield put(
      setHasHiddenTransactions(
        transactionCount > 0 && newTransactionCount < transactionCount
      )
    );
  }
}

export function* handleGetExtendedCardForPerson(action: any): any {
  try {
    const cardClient = setUpClient();
    const response: ExtendedCardDTO = yield cardClient.getExtendedCardForPerson(
      action.params.carePrincipalId,
      action.params.personId,
      action.params.cardTypeId
    );
    console.log(response);
    yield filterAllTransactions(response);
    yield put(setExtendedCardForPerson(response));
  } catch (error) {
    console.log(error);
    yield handleError(error);
  }
}

export function* handleGetExtendedCard(action: any): any {
  try {
    const cardClient = setUpClient();
    const response = yield cardClient.getExtendedCard(
      action.params.carePrincipalId,
      action.params.cardSystemId
    );
    yield filterAllTransactions(response);

    yield put(setExtendedCard(response));
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleGetCardsAsPdf(action: any): any {
  try {
    const cardClient = setUpClient();
    const response = yield cardClient.getCardsAsPdf(
      action.params.carePrincipalId,
      action.params.cardSystemIds,
      action.params.careProviderId,
      action.params.isPdfSummary,
      action.params.patientName
    );
    const FileSaver = require("file-saver");
    const datetime = moment().format("yyyy-MM-YYYY HH:mm:ss");
    const blob = new Blob([response], {
      type: "application/pdf;charset=utf-8",
    });
    FileSaver.saveAs(blob, "EFK-Card " + datetime);
  } catch (error) {
    yield handleError(error);
  }
}

function* createInfoMessages(personId: string, card: CardWithHistoryDTO) {
  const amountToLimit = card?.current?.amountToLimit;
  const sum = card?.current?.currentAmount;
  const elderAge = store.getState()?.settings?.internalSettings?.elderAge;
  yield put({ type: "RESET_INFO_MESSAGES" });
  if (isPNRandElder(personId, elderAge)) {
    yield put(
      addInfoMessage(i18n.t("info.elder", { id: personId, elderAge: elderAge }))
    );
  }
  if (amountToLimit && sum && amountToLimit < 0) {
    yield put(
      addInfoMessage(
        i18n.t("info.overcharged", { sum: sum, limit: sum + amountToLimit })
      )
    );
  }
}

export function* handleGetCardWithHistoryForPerson(action: any): any {
  try {
    yield put(setLoading(true));
    const cardClient = setUpClient();
    const personId = action.params.personId;

    const response: CardWithHistoryDTO =
      yield cardClient.getCardWithHistoryForPerson(
        action.params.carePrincipalId,
        personId,
        action.params.cardTypeId
      );
    response.history?.sort((a: any, b: any) => {
      if (a.endDate < b.endDate)
        return 1;
      if (a.endDate > b.endDate)
        return -1;
      return 0;
    })
    yield createInfoMessages(personId, response);
    yield put(setCardWithHistory(response));
  } catch (error: any) {
    if (error.type) {
      let status = 0;
      let message = "";
      yield error.json().then(async (body: any) => {
        status = body.status;
        message = body.messages[0];
      });
      if (status === 406) {
        if (message.includes("inträde")) {
          const isConfirmed = yield call(confirmSaga, "optIn");
          if (isConfirmed) {
            yield handleRegisterOptIn(action);
            yield handleGetCardWithHistoryForPerson(action);
          } else {
            yield handleRegisterOptOut(action);
          }
        } else {
          const isConfirmed = yield call(confirmSaga, "optOut");
          if (isConfirmed) {
            yield handleRegisterOptIn(action);
            yield handleGetCardWithHistoryForPerson(action);
          }
        }
      }
    } else {
     yield handleError(error);
    } 
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleRegisterCard(action: any): any {
  try {
    const params = action.params;
    const cardClient = setUpClient();
    const response = yield cardClient.registerCard(params);
    yield put({ type: "MODAL_CONFIRM" });
    const newCard = yield cardClient.getCardWithHistoryForPerson(
      response.carePrincipal.id,
      action.params.personId,
      action.params.cardTypeId
    );
    yield put(setCardWithHistory(newCard));
    toast.success(String(i18n.t("toasts.registerCard")));
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleChangeCardModal(action: any): any {
  try {
    yield call(confirmSaga, "changeCard");
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleDeleteCardModal(action: any): any {
  try {
    const isConfirmed = yield call(confirmSaga, "delete");
    if (isConfirmed) {
      yield call(handleDeleteCard, action);
    }
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleOptInModal(action: any): any {
  try {
    yield call(confirmSaga, "optIn");
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleChangeCard(action: any): any {
  try {
    const updateCard = action.changeCard;
    const cardClient = setUpClient();
    const response = yield cardClient.updateCard(updateCard);
    yield put({ type: "MODAL_CONFIRM" });
    const newCard = yield cardClient.getCardWithHistoryForPerson(
      response.carePrincipal.id,
      action.personId,
      response.cardType.id
    );
    yield put(setCardWithHistory(newCard));
    const params = {
      cardSystemId: updateCard.cardSystemId,
      carePrincipalId: updateCard.carePrincipalId
    };
    yield put({ type: GET_EXTENDED_CARD, params });
    toast.success(String(i18n.t("toasts.changeCard")));
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleOrderCard(action: any): any {
  try {
    const isConfirmed = yield call(confirmSaga, "orderCard");
    if (isConfirmed) {
      const card = action.params;
      const cardClient = setUpClient();
      yield cardClient.orderCard(card);
      toast.success(String(i18n.t("toasts.orderCard")));
    }
  } catch (error: any) {
    yield handleError(error);
  }
}

export function* handleGetAllCardsForPerson(action: any): any {
  try {
    const cardClient = setUpClient();
    const response = yield cardClient.getAllCardForPerson(
      action.params.personId,
      action.params.carePrincipalId
    );
    yield put(setExtendedCards(response));
    getSuccessToast(i18n.t("toasts.getCard"));
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleGetCardsForId(action: any): any {
  try {
    const cardClient = setUpClient();
    const response = yield cardClient.getAllCardForId(
      action.params.cardId,
      action.params.carePrincipalId
    );
    yield put(setExtendedCards(response));
    getSuccessToast(i18n.t("toasts.getCard"));
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleDeleteCard(action: any): any {
  try {
    const cardClient = setUpClient();
    yield cardClient.deleteCard(
      action.params.carePrincipalId,
      action.params.cardSystemId
    );
    yield getCardsAfterAction(action);
    getSuccessToast(i18n.t("toasts.deleteCard"));
    if(window.location.href.includes("history")){
      window.location.href = "/search-card";
    }
  } catch (error) {
    yield handleError(error);
  }
}

export function* handleResetCard(action: any): any {
  try {
    const isConfirmed = yield call(confirmSaga, "reset");
    if (isConfirmed) {
      const cardClient = setUpClient();
      yield cardClient.resetCard(
        action.params.carePrincipalId,
        action.params.cardSystemId
      );
      yield getCardsAfterAction(action);
      getSuccessToast(i18n.t("toasts.resetCard"));
    }
  } catch (error) {
    yield handleError(error);
  }
}
